import React, { ChangeEvent } from 'react';

import {
  AutocompleteRenderInputParams,
  TextField,
  TextFieldProps,
} from '@mui/material';

import { Preloader } from '../common';
import { InputMask } from '../../InputMask';

import useStyles from './SuggestedInput.styles';

interface TextFieldMaskedOwnProps {
  mask: string;
  loading?: boolean;
  onChange: (event: ChangeEvent<HTMLInputElement>, nextValue: string) => void;
}

type TextFieldMaskedProps = TextFieldMaskedOwnProps &
  AutocompleteRenderInputParams &
  Pick<TextFieldProps, 'name' | 'error' | 'helperText' | 'placeholder'>;

/**
 * Соответствие символов формата регулярным выражениям.
 * Используется компонентом InputMask для определения правил маски.
 */
const formatChars = {
  '9': '[0-9]',
  a: '[A-Za-z]',
  '*': '[A-Za-z0-9А-Яа-я]',
  я: '[А-Яа-я]',
};

/**
 * TextFieldMasked — компонент, который предоставляет поле ввода с маской, используя Material-UI's TextField.
 * Он поддерживает различные форматы масок и может отображать индикатор загрузки.
 *
 * @param {Object} props - Свойства компонента.
 * @param {string} props.mask - Маска, которая будет применена к полю ввода.
 * @param {boolean} [props.loading=false] - Если true, поле ввода будет показывать индикатор загрузки.
 * @param {(event: ChangeEvent<HTMLInputElement>, nextValue: string) => void} props.onChange - Функция обратного вызова для обработки изменений значения ввода.
 * @param {string} [props.name] - Атрибут name для поля ввода.
 * @param {boolean} [props.error=false] - Если true, поле ввода будет отмечено как с ошибкой.
 * @param {string | ReactNode} [props.helperText] - Вспомогательный текст, который будет отображаться с полем ввода.
 * @param {TextFieldProps} [props.rest] - Дополнительные свойства для Material-UI TextField.
 * @returns {JSX.Element} - JSX компонента.
 */

export const TextFieldMasked = ({
  mask,
  name,
  error,
  helperText,
  loading,
  onChange,
  inputProps,
  InputProps,
  InputLabelProps,
  ...rest
}: TextFieldMaskedProps) => {
  const { classes } = useStyles();

  const { value, ...inputPropsRest } = inputProps;

  /**
   * Обрабатывает изменения значения в поле ввода.
   * Вызывает предоставленную функцию onChange с событием и новым значением ввода.
   *
   * @param {ChangeEvent<HTMLInputElement>} e - Событие изменения.
   */

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(e, e.target.value);
    inputProps.onChange?.(e);
  };

  return (
    <InputMask
      {...{ formatChars }}
      value={inputProps.value}
      onFocus={inputProps?.onFocus}
      onBlur={inputProps?.onBlur}
      onMouseDown={inputProps?.onMouseDown}
      onKeyDown={inputProps?.onKeyDown}
      mask={mask}
      name={name}
      onChange={handleChange}
      disabled={rest.disabled}
    >
      <TextField
        {...rest}
        inputRef={InputProps.ref}
        name={name}
        classes={{ root: classes.inputRoot }}
        autoComplete="off"
        variant="outlined"
        fullWidth
        error={error}
        helperText={helperText}
        placeholder={rest.placeholder}
        slotProps={{
          htmlInput: {
            ...inputPropsRest,
          },
          input: {
            ...InputProps,
            ...(loading ? { endAdornment: <Preloader /> } : {}),
          },
          inputLabel: {
            ...InputLabelProps,
          },
        }}
      />
    </InputMask>
  );
};
